import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { sortBy } from 'webapp/utils/common';

import type { GoalsBaseArgs } from 'webapp/components/goals/base';
import GoalsBaseComponent from 'webapp/components/goals/base';

import type { Group } from 'webapp/models-custom';
import type { DeleteAction } from 'webapp/models-custom/batch-select';
import BatchSelect from 'webapp/models-custom/batch-select';
import type FilterSortGoalInGrid from 'webapp/models-custom/filter-sort/goal-teams-in-grid';
import type FilterSortGoalInList from 'webapp/models-custom/filter-sort/goal-teams-in-list';
import type Goal from 'webapp/models-custom/goal';

export interface GoalsTeamArgs extends GoalsBaseArgs {
	onAdd: (teamId?: string) => void;
}

export type SectionToggler = Record<string, boolean>;
export default abstract class GoalsCompanyBaseComponent extends GoalsBaseComponent<GoalsTeamArgs> {
	@service('request') declare requestService: Service.Request;
	@service('model-manager/groups') declare mmGroupsService: Service.ModelManager.Groups;

	abstract filterSort: FilterSortGoalInGrid | FilterSortGoalInList;

	batchSelect: BatchSelect<Goal>;

	@tracked sectionToggler!: SectionToggler;

	constructor(owner: Owner, args: GoalsTeamArgs) {
		super(owner, args);

		this.batchSelect = new BatchSelect<Goal>({
			getAllItems: () => {
				return this.filterSort.results;
			},
			getOptions: () => {
				return this.batchOptions;
			},
			doBatchAction: (actionName) => {
				return this.doBatchAction(actionName as DeleteAction);
			},
		});

		this.sectionToggler = {};
		this.getTeamsFromGoals(this.model).forEach((team) => {
			this.sectionToggler[team.id!] = true;
		});
	}

	@cached
	get teamModels() {
		const teamsFromGoals = this.getTeamsFromGoals(this.filterSort.results);
		return sortBy(teamsFromGoals, 'id').reverse();
	}

	@action
	toggleSection(teamId: string) {
		this.sectionToggler[teamId] = !this.sectionToggler[teamId];
		this.sectionToggler = this.sectionToggler;
		schedule('afterRender', this, this.doVisibleCheck);
	}

	@action
	getSectionToggleState(teamId: string): boolean {
		return this.sectionToggler[teamId]!;
	}

	@action
	favoriteGoalsFilterForTeamFn(groupId: string) {
		return (items: Goal[]) => {
			return items.filter((item: Goal) => item.favorite && item.groupId === groupId);
		};
	}

	@action
	nonFavoriteGoalsFilterForTeamFn(groupId: string) {
		return (items: Goal[]) => {
			return items.filter((item: Goal) => !item.favorite && item.groupId === groupId);
		};
	}

	@action
	hasFavoriteGoalsForTeam(groupId: string): boolean {
		return this.model.filter((item) => item.favorite && item.groupId === groupId).length > 0;
	}

	private getTeamsFromGoals(goals: Goal[]): Group[] {
		const groupIds = new Set(goals.map((goal) => goal.groupId));
		return this.mmGroupsService.groupModels.filter((group) => groupIds.has(group.id));
	}
}
